import Group from 'components/utils/Group'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import styled from 'styled-components'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DndContext, DragOverlay } from '@dnd-kit/core'
import noop from 'lib/function/noop'
import zIndex from 'styles/tools/z-index'
import WhileDraggingStyle from 'tripPlanner/components/DragAndDrop/WhileDraggingStyle'
import ItineraryItemCard from './ItineraryItemCard'
import { useTripPlanCanvasDragReorder } from './useTripPlanCanvasDragReorder'
import { TimelineDragContext, type TimelineDragContextValue } from './TripPlanCanvasDragContext'
import { DROP_ANIMATION_DURATION, timelineAutoScrollConfig, timelineDropAnimationSideEffects, type TimelineMove } from './tripPlanCanvasReorder'
import type {
  HotelMapViewData,
  ExperienceMapViewData,
  TripItineraryData,
  TripStop,
  TripDay,
  TripItineraryItem,
  TripItemWithoutDay,
  ManualItineraryDeleteRequest,
  TransportConnection,
} from 'tripPlanner/api/types'
import { isFlightCardSavedItemData } from 'tripPlanner/api/types'
import { isBookedFlightOption } from './ShortlistedFlightsSection/flightShortlistUtils'
import type { LEExploreCategorySource } from 'tripPlanner/pages/chat/chatExploreCategories'
import TripPlanCanvasItem from './TripPlanCanvasItem'
import TripPlanTransportLine from './TripPlanTransportLine'
import { shouldHideTimelineOriginStop } from './tripPlanCanvasContent'
import type { ItineraryScrollTarget } from '../hooks/useItineraryUpdates'
import { useItineraryScrollTarget } from '../hooks/useItineraryScrollTarget'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { getCruiseSavedOfferId } from 'tripPlanner/utils/savedItemDataGuards'
import {
  CanvasContainer,
  CanvasContent,
  ScrollableContainer,
  SkeletonStopSheet,
  FullWidthLoadingBox,
  DottedConnector,
  ItemSpacer,
  StopContentWrapper,
} from './TripPlanCanvas.styles'
import ChatItineraryDateHeader from './ChatItineraryDateHeader'
import type { Highlight } from './tripPlanCanvasTypes'

export type { Highlight } from './tripPlanCanvasTypes'

const TRIP_PLAN_SCROLL_SELECTORS = {
  item: (target: ItineraryScrollTarget) => target.itemId !== undefined ? `div[data-item-id="${target.itemId}"]` : undefined,
  day: (target: ItineraryScrollTarget) => target.dayId !== undefined ? `[data-day-id="${target.dayId}"]` : undefined,
  stop: (target: ItineraryScrollTarget) => `[data-stop-id="${target.stopId}"]`,
}

const DAY_SECTION_SELECTOR = '[data-itinerary-day-section="true"]'

const DragOverlayContent = styled.div`
  opacity: 0.7;
  cursor: grabbing;
  pointer-events: none;
`

interface EnrichedStop {
  stop: TripStop
  itineraryDayCount: number
  stopHighlights: Array<Highlight>
  transports?: Array<TransportConnection>
  previousHadTransport: boolean
  showLeadingSpacer: boolean
}

function addCruiseItemOfferIds(
  offerIds: Set<string>,
  items: ReadonlyArray<TripItineraryItem | TripItemWithoutDay>,
) {
  for (const item of items) {
    const offerId = getCruiseSavedOfferId(item.savedItemData)
    if (offerId) offerIds.add(offerId)
  }
}

function getVisibleCruiseItemOfferIds(stops: ReadonlyArray<TripStop>): Set<string> {
  const offerIds = new Set<string>()

  for (const stop of stops) {
    for (const day of stop.days ?? []) {
      addCruiseItemOfferIds(offerIds, day.items ?? [])
    }

    addCruiseItemOfferIds(offerIds, stop.itemsWithoutDay ?? [])
  }

  return offerIds
}

function getCanvasTransport(
  transport: TransportConnection,
  visibleCruiseItemOfferIds: Set<string>,
  renderedCruiseOfferIds: Set<string>,
): TransportConnection | undefined {
  if (transport.options.length === 0) return undefined

  const filteredOptions = transport.options.filter((option) => {
    const cruiseOfferId = getCruiseSavedOfferId(option.savedItemData)
    if (!cruiseOfferId) return true

    if (visibleCruiseItemOfferIds.has(cruiseOfferId) || renderedCruiseOfferIds.has(cruiseOfferId)) {
      return false
    }

    renderedCruiseOfferIds.add(cruiseOfferId)
    return true
  })

  if (filteredOptions.length === 0) return undefined
  if (filteredOptions.length === transport.options.length) return transport

  return {
    ...transport,
    options: filteredOptions,
  }
}

interface Props {
  data?: TripItineraryData
  onLearnMoreClick?: (data: { googlePlaceId: string; placeName: string; countryName?: string; stopId?: number }) => void
  onRefetchItinerary?: () => void
  onSendMessage?: (message: string) => Promise<void> | void
  onDeleteTarget?: (target: ManualItineraryDeleteRequest) => Promise<void> | void
  onExploreMap?: (coordinates: { lat: number; lng: number }, source: LEExploreCategorySource) => void
  onShowMap?: (data: HotelMapViewData) => void
  onShowExperienceOnMap?: (data: ExperienceMapViewData) => void
  enrichingStopIds?: Set<number>
  onAskAI?: (dayNumber: number, dayTitle: string, date?: string) => void
  onAskAIForItem?: (item: TripItineraryItem | TripItemWithoutDay) => void
  onMoveItem?: (itemId: number, move: TimelineMove) => void | boolean | Promise<void | boolean>
  scrollTarget?: ItineraryScrollTarget | undefined
  onScrollComplete?: () => void
  drawerCloseSignal?: number
  onDrawerOpenChange?: (isOpen: boolean) => void
  stopTimingEditVariant?: 'default' | 'mobile'
}

interface TripPlanCanvasStopSectionProps extends EnrichedStop {
  data?: TripItineraryData
  onLearnMoreClick?: Props['onLearnMoreClick']
  onRefetchItinerary?: Props['onRefetchItinerary']
  onSendMessage?: Props['onSendMessage']
  onDeleteTarget?: Props['onDeleteTarget']
  onExploreMap?: Props['onExploreMap']
  onShowMap?: Props['onShowMap']
  onShowExperienceOnMap?: Props['onShowExperienceOnMap']
  enrichingStopIds?: Props['enrichingStopIds']
  onAskAI?: Props['onAskAI']
  onAskAIForItem?: Props['onAskAIForItem']
  scrollTarget?: Props['scrollTarget']
  drawerCloseSignal?: Props['drawerCloseSignal']
  onDrawerOpenChange?: Props['onDrawerOpenChange']
  stopTimingEditVariant?: Props['stopTimingEditVariant']
}

function TripPlanCanvasStopSection({
  data,
  stop,
  itineraryDayCount,
  stopHighlights,
  transports,
  previousHadTransport,
  showLeadingSpacer,
  onLearnMoreClick,
  onRefetchItinerary,
  onSendMessage,
  onDeleteTarget,
  onExploreMap,
  onShowMap,
  onShowExperienceOnMap,
  enrichingStopIds,
  onAskAI,
  onAskAIForItem,
  scrollTarget,
  drawerCloseSignal,
  onDrawerOpenChange,
  stopTimingEditVariant,
}: TripPlanCanvasStopSectionProps) {
  const hasTransport = transports?.some(transport => transport.options.length > 0)
  const hideStop = shouldHideTimelineOriginStop(stop, stopHighlights.length)
  const stopCoordinates = useMemo(() => (
    stop.stopPlaceDetail ? { lat: stop.stopPlaceDetail.lat, lng: stop.stopPlaceDetail.lng } : undefined
  ), [stop.stopPlaceDetail])
  const showStopScrollTarget = scrollTarget?.stopId === stop.id

  return (
    <>
      {showLeadingSpacer && previousHadTransport && <DottedConnector />}
      {showLeadingSpacer && !previousHadTransport && <ItemSpacer />}
      {!hideStop && (
        <StopContentWrapper data-stop-id={stop.id}>
          <TripPlanCanvasItem
            data={data}
            stopId={stop.id}
            placeName={stop.placeName}
            placeCountry={stop.placeCountry}
            itineraryDayCount={itineraryDayCount}
            description={stop.description}
            imageId={stop.imageIds?.[0] ?? ''}
            stopSeq={stop.stopSeq}
            highlights={stopHighlights}
            googlePlaceId={stop.stopPlaceDetail?.placeId}
            onLearnMoreClick={onLearnMoreClick}
            onSendMessage={onSendMessage}
            onDeleteTarget={onDeleteTarget}
            onExploreMap={onExploreMap}
            onShowMap={onShowMap}
            onShowExperienceOnMap={onShowExperienceOnMap}
            stopCoordinates={stopCoordinates}
            isEnriching={enrichingStopIds?.has(stop.id)}
            onAskAI={onAskAI}
            onAskAIForItem={onAskAIForItem}
            scrollTargetDayId={showStopScrollTarget ? scrollTarget?.dayId : undefined}
            scrollTargetItemId={showStopScrollTarget ? scrollTarget?.itemId : undefined}
            drawerCloseSignal={drawerCloseSignal}
            onDrawerOpenChange={onDrawerOpenChange}
            stopTimingEditVariant={stopTimingEditVariant}
          />
        </StopContentWrapper>
      )}
      {hasTransport && transports?.map((transport) =>
        transport.options.length > 0 && (
          <Fragment key={`${transport.departureStopId}-${transport.arrivalStopId}`}>
            {!hideStop && <DottedConnector />}
            <TripPlanTransportLine transport={transport} onRefetchItinerary={onRefetchItinerary} onSendMessage={onSendMessage} onDeleteTarget={onDeleteTarget} />
          </Fragment>
        ),
      )}
    </>
  )
}

function TripPlanCanvas(props: Props) {
  const { data, onLearnMoreClick, onRefetchItinerary, onSendMessage, onDeleteTarget, onExploreMap, onShowMap, onShowExperienceOnMap, enrichingStopIds, onAskAI, onAskAIForItem, onMoveItem, scrollTarget, onScrollComplete, drawerCloseSignal, onDrawerOpenChange, stopTimingEditVariant = 'default' } = props
  const { isStreaming, isStreamOpen } = useChatStreamStatus()
  const { isManualItineraryDeletePending, isManualItineraryMovePending, isFlightCartMutationPending } = useItineraryContext()

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const dateHeaderRef = useRef<HTMLDivElement>(null)
  const pendingDateHeaderDayIdRef = useRef<number | undefined>(undefined)
  const pendingDateHeaderScrollTimeoutRef = useRef<number | undefined>(undefined)
  const [selectedDayId, setSelectedDayId] = useState<number | undefined>(undefined)

  useItineraryScrollTarget({
    scrollTarget,
    containerRef: scrollContainerRef,
    selectors: TRIP_PLAN_SCROLL_SELECTORS,
    onScrollComplete,
  })

  const canonicalStops = useMemo(() => data?.stops ?? [], [data])

  // Mirror the list view's dragActionsDisabled: don't allow a drag to start while
  // any itinerary mutation is streaming or in flight, otherwise the drop optimistically
  // lands and then silently rolls back when the guarded move mutation rejects it.
  const dragActionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending || isManualItineraryMovePending || isFlightCartMutationPending
  const dragEnabled = !dragActionsDisabled && Boolean(onMoveItem)
  const {
    orderedStops,
    activeItem,
    overItemId,
    dropPlacement,
    overDayId,
    overUnplannedStopId,
    sensors,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
  } = useTripPlanCanvasDragReorder({
    stops: canonicalStops,
    disabled: !dragEnabled,
    onMoveItem: onMoveItem ?? noop,
  })
  const dragContextValue = useMemo<TimelineDragContextValue>(() => ({
    dragEnabled,
    activeItemId: activeItem?.id,
    overItemId,
    dropPlacement,
    overDayId,
    overUnplannedStopId,
  }), [dragEnabled, activeItem?.id, overItemId, dropPlacement, overDayId, overUnplannedStopId])

  // Render from the optimistic order so a drop lands instantly; downstream stop
  // components read the same order via `optimisticData`.
  const stops = orderedStops
  const optimisticData = useMemo(() => (data ? { ...data, stops } : data), [data, stops])
  const visibleCruiseItemOfferIds = useMemo(() => getVisibleCruiseItemOfferIds(stops), [stops])

  const highlightsByStop = useMemo(() => {
    return stops.reduce<Record<number, Array<Highlight>>>((acc, stop: TripStop) => {
      const highlights: Array<Highlight> = []

      stop.itemsWithoutDay?.forEach((item: TripItemWithoutDay) => {
        if (item.isHighlight) {
          highlights.push({
            id: item.id,
            name: item.name,
            emoji: item.emoji,
            description: item.description,
            googlePlaceId: item.itemPlaceDetail?.placeId,
            savedItemData: item.savedItemData,
          })
        }
      })

      stop.days.forEach((day: TripDay) => {
        day.items.forEach((item: TripItineraryItem) => {
          if (item.isHighlight) {
            highlights.push({
              id: item.id,
              name: item.name,
              emoji: item.emoji,
              description: item.description,
              googlePlaceId: item.itemPlaceDetail?.placeId,
              savedItemData: item.savedItemData,
            })
          }
        })
      })

      const hasDays = stop.days.length > 0
      const hasItemsWithoutDay = (stop.itemsWithoutDay?.length ?? 0) > 0
      if (highlights.length === 0 && !hasDays && hasItemsWithoutDay) {
        stop.itemsWithoutDay?.forEach((item: TripItemWithoutDay) => {
          highlights.push({
            id: item.id,
            name: item.name,
            emoji: item.emoji,
            description: item.description,
            googlePlaceId: item.itemPlaceDetail?.placeId,
            savedItemData: item.savedItemData,
          })
        })
      }

      if (highlights.length > 0) {
        acc[stop.id] = highlights
      }

      return acc
    }, {})
  }, [stops])

  const enrichedStops = useMemo(() => {
    const renderedCruiseOfferIds = new Set<string>()

    let previousHadTransport = false

    return stops.map((stop: TripStop, index) => {
      const itineraryDayCount = stop.days?.length ?? 0
      const stopHighlights = highlightsByStop[stop.id] ?? []
      const transports = stop.transports?.flatMap((transport) => {
        const visibleTransport = getCanvasTransport(transport, visibleCruiseItemOfferIds, renderedCruiseOfferIds)
        return visibleTransport ? [visibleTransport] : []
      })
      const hasTransport = transports?.some(transport => transport.options.length > 0) ?? false
      const enrichedStop = {
        stop,
        itineraryDayCount,
        stopHighlights,
        transports,
        previousHadTransport,
        showLeadingSpacer: index > 0,
      }

      previousHadTransport = hasTransport
      return enrichedStop
    })
  }, [highlightsByStop, stops, visibleCruiseItemOfferIds])

  const dateHeaderStops = useMemo(() => {
    return enrichedStops.flatMap(({ stop, stopHighlights }) => {
      const hideStop = shouldHideTimelineOriginStop(stop, stopHighlights.length)

      if (hideStop || stop.days.length === 0) {
        return []
      }

      return [{
        id: stop.id,
        placeName: stop.placeName,
        days: stop.days,
      }]
    })
  }, [enrichedStops])

  const firstDateHeaderDayId = dateHeaderStops[0]?.days[0]?.id
  const dateHeaderDayIds = useMemo(() => (
    dateHeaderStops.flatMap(stop => stop.days.map(day => day.id))
  ), [dateHeaderStops])

  const dateHeaderDayIdsKey = dateHeaderDayIds.join('|')

  const clearPendingDateHeaderDay = useCallback(() => {
    pendingDateHeaderDayIdRef.current = undefined
    if (pendingDateHeaderScrollTimeoutRef.current !== undefined) {
      window.clearTimeout(pendingDateHeaderScrollTimeoutRef.current)
      pendingDateHeaderScrollTimeoutRef.current = undefined
    }
  }, [])

  const schedulePendingDateHeaderDayRelease = useCallback(() => {
    if (pendingDateHeaderDayIdRef.current === undefined) return
    if (pendingDateHeaderScrollTimeoutRef.current !== undefined) {
      window.clearTimeout(pendingDateHeaderScrollTimeoutRef.current)
    }
    pendingDateHeaderScrollTimeoutRef.current = window.setTimeout(clearPendingDateHeaderDay, 150)
  }, [clearPendingDateHeaderDay])

  const updateSelectedDayFromScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const daySections = Array.from(container.querySelectorAll<HTMLElement>(DAY_SECTION_SELECTOR))
    if (daySections.length === 0) return

    const headerHeight = dateHeaderRef.current?.offsetHeight ?? 0
    const containerTop = container.getBoundingClientRect().top
    const activationY = containerTop + headerHeight + 16
    let activeSection = daySections[0]

    for (const section of daySections) {
      const sectionRect = section.getBoundingClientRect()
      if (sectionRect.top > activationY) {
        break
      }
      activeSection = section
    }

    const nextDayId = Number(activeSection.dataset.dayId)
    if (Number.isFinite(nextDayId)) {
      const pendingDayId = pendingDateHeaderDayIdRef.current
      if (pendingDayId !== undefined) {
        if (nextDayId === pendingDayId) {
          clearPendingDateHeaderDay()
        } else {
          return
        }
      }

      setSelectedDayId(previousDayId => previousDayId === nextDayId ? previousDayId : nextDayId)
    }
  }, [clearPendingDateHeaderDay])

  const handleDateHeaderDayClick = useCallback((dayId: number) => {
    setSelectedDayId(dayId)

    const container = scrollContainerRef.current
    if (!container) return

    const daySection = container.querySelector<HTMLElement>(`${DAY_SECTION_SELECTOR}[data-day-id="${dayId}"]`)
    if (!daySection) return

    pendingDateHeaderDayIdRef.current = dayId
    const headerHeight = dateHeaderRef.current?.offsetHeight ?? 0
    const containerRect = container.getBoundingClientRect()
    const dayRect = daySection.getBoundingClientRect()
    const nextScrollTop = container.scrollTop + dayRect.top - containerRect.top - headerHeight - 8
    container.scrollTo({
      top: Math.max(0, nextScrollTop),
      behavior: 'smooth',
    })
    schedulePendingDateHeaderDayRelease()
  }, [schedulePendingDateHeaderDayRelease])

  useEffect(() => {
    setSelectedDayId(previousDayId => {
      if (previousDayId !== undefined && dateHeaderDayIds.includes(previousDayId)) {
        return previousDayId
      }

      return firstDateHeaderDayId
    })
  }, [dateHeaderDayIds, firstDateHeaderDayId])

  useEffect(() => {
    if (scrollTarget?.dayId !== undefined && dateHeaderDayIds.includes(scrollTarget.dayId)) {
      setSelectedDayId(scrollTarget.dayId)
    }
  }, [dateHeaderDayIds, scrollTarget?.dayId])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    let animationFrame: number | undefined

    const handleScroll = () => {
      schedulePendingDateHeaderDayRelease()
      if (animationFrame !== undefined) return

      animationFrame = requestAnimationFrame(() => {
        animationFrame = undefined
        updateSelectedDayFromScroll()
      })
    }

    const initialFrame = requestAnimationFrame(updateSelectedDayFromScroll)
    container.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      cancelAnimationFrame(initialFrame)
      if (animationFrame !== undefined) {
        cancelAnimationFrame(animationFrame)
      }
      clearPendingDateHeaderDay()
      container.removeEventListener('scroll', handleScroll)
    }
  }, [clearPendingDateHeaderDay, dateHeaderDayIdsKey, schedulePendingDateHeaderDayRelease, updateSelectedDayFromScroll])

  // A booked return flight is modelled as one transport (with a returnLeg). Surface it again
  // at the end of the itinerary as a reversed tile (return direction) so the trip reads round-trip.
  const bookedReturnTransport = useMemo((): TransportConnection | undefined => {
    for (const { transports } of enrichedStops) {
      const match = transports?.find(transport =>
        transport.options.some(option =>
          isFlightCardSavedItemData(option.savedItemData) && isBookedFlightOption(option) && Boolean(option.returnLeg),
        ),
      )
      if (match) return match
    }
    return undefined
  }, [enrichedStops])

  // Heading for the reversed return tile, built from stop place names (arrival → departure)
  // rather than reversing the display string, which is more robust to naming formats.
  const reversedReturnTitle = useMemo((): string | undefined => {
    if (!bookedReturnTransport) return undefined
    const placeNameByStopId = new Map(stops.map(stop => [stop.id, stop.placeName]))
    const from = placeNameByStopId.get(bookedReturnTransport.arrivalStopId)
    const to = placeNameByStopId.get(bookedReturnTransport.departureStopId)
    return from && to ? `${from} → ${to}` : undefined
  }, [bookedReturnTransport, stops])

  const hasStops = stops.length > 0

  if (isStreaming && !hasStops) {
    return (
      <CanvasContainer>
        <ScrollableContainer>
          <CanvasContent>
            <Group direction="vertical" gap={40} horizontalAlign="center">
              <SkeletonStopSheet padding={20} border="light-foreground">
                <Group direction="vertical" gap={20}>
                  <Group direction="vertical" gap={12}>
                    <Group direction="horizontal" gap={12} verticalAlign="start">
                      <LoadingBox width={56} height={56} borderRadius="S" />
                      <Group direction="vertical" gap={4}>
                        <LoadingBox width={160} height={24} borderRadius="XS" />
                        <LoadingBox width={60} height={18} borderRadius="XS" />
                      </Group>
                    </Group>
                    <Group direction="vertical" gap={8}>
                      {Array.from({ length: 3 }).map((_, index) => (
                        <Fragment key={index}>
                          <LoadingBox width={220} height={16} borderRadius="XS" />
                          <FullWidthLoadingBox height={16} borderRadius="XS" />
                        </Fragment>
                      ))}
                    </Group>
                  </Group>
                </Group>
              </SkeletonStopSheet>
            </Group>
          </CanvasContent>
        </ScrollableContainer>
      </CanvasContainer>
    )
  }

  return (
    <TimelineDragContext.Provider value={dragContextValue}>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        autoScroll={timelineAutoScrollConfig}
      >
        <CanvasContainer>
          <ScrollableContainer ref={scrollContainerRef}>
            <ChatItineraryDateHeader
              ref={dateHeaderRef}
              stops={dateHeaderStops}
              selectedDayId={selectedDayId}
              onDayClick={handleDateHeaderDayClick}
            />
            <CanvasContent>
              <Group direction="vertical" gap={0} horizontalAlign="center">
                {enrichedStops.map(enrichedStop => (
                  <TripPlanCanvasStopSection
                    key={enrichedStop.stop.id}
                    {...enrichedStop}
                    data={optimisticData}
                    onLearnMoreClick={onLearnMoreClick}
                    onRefetchItinerary={onRefetchItinerary}
                    onSendMessage={onSendMessage}
                    onDeleteTarget={onDeleteTarget}
                    onExploreMap={onExploreMap}
                    onShowMap={onShowMap}
                    onShowExperienceOnMap={onShowExperienceOnMap}
                    enrichingStopIds={enrichingStopIds}
                    onAskAI={onAskAI}
                    onAskAIForItem={onAskAIForItem}
                    scrollTarget={scrollTarget}
                    drawerCloseSignal={drawerCloseSignal}
                    onDrawerOpenChange={onDrawerOpenChange}
                    stopTimingEditVariant={stopTimingEditVariant}
                  />
                ))}
                {bookedReturnTransport && (
                  <>
                    <DottedConnector />
                    <TripPlanTransportLine
                      transport={bookedReturnTransport}
                      reversed
                      title={reversedReturnTitle}
                      onRefetchItinerary={onRefetchItinerary}
                      onSendMessage={onSendMessage}
                      onDeleteTarget={onDeleteTarget}
                    />
                  </>
                )}
              </Group>
            </CanvasContent>
          </ScrollableContainer>
        </CanvasContainer>
        <DragOverlay
          dropAnimation={{ duration: DROP_ANIMATION_DURATION, easing: 'ease-out', sideEffects: timelineDropAnimationSideEffects }}
          zIndex={zIndex.draggingItem}
        >
          {activeItem && (
            <DragOverlayContent>
              <ItineraryItemCard item={activeItem} />
              <WhileDraggingStyle />
            </DragOverlayContent>
          )}
        </DragOverlay>
      </DndContext>
    </TimelineDragContext.Provider>
  )
}

export default TripPlanCanvas
